import React, { useCallback, useEffect, useState } from 'react';
import { useEnterpriseManage } from './EnterpriseManageContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import EnterpriseRoleBadge from '../../components/enterprise/EnterpriseRoleBadge';
import Icon from '../../components/AppIcon';
import CompanyMemberHubupQrModal from '../../components/hubup/CompanyMemberHubupQrModal';
import {
  fetchEnterpriseMembers,
  fetchEnterpriseRoles,
  fetchEnterpriseMemberHubupQr,
  inviteEnterpriseMember,
  removeEnterpriseMember,
  updateEnterpriseMember,
} from '../../api/enterprises';
import { useUserAccess } from '../../context/UserAccessContext';
import { isPlatformAdmin } from '../../utils/platformAdmin';

export default function EnterpriseTeamTab() {
  const { enterpriseId } = useEnterpriseManage();
  const { user } = useUserAccess();
  const platformAdmin = isPlatformAdmin(user);
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [qrMember, setQrMember] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [memRes, rolesRes] = await Promise.all([
        fetchEnterpriseMembers(enterpriseId),
        fetchEnterpriseRoles(enterpriseId),
      ]);
      const roleList = rolesRes?.data?.roles || [];
      setRoles(roleList);
      setMembers(memRes?.data?.members || []);
    } catch (e) {
      setError(e?.message || 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [enterpriseId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (inviteRoleId || !roles.length) return;
    const def = roles.find((r) => !r.isSystemAdmin) || roles[0];
    setInviteRoleId(def?.roleId || '');
  }, [roles, inviteRoleId]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim() || !inviteRoleId) return;
    setSaving(true);
    setError('');
    try {
      await inviteEnterpriseMember(enterpriseId, {
        email: email.trim(),
        password: password.trim(),
        enterpriseRoleId: inviteRoleId,
      });
      setEmail('');
      setPassword('');
      setShowInvite(false);
      await load();
    } catch (e) {
      setError(e?.message || 'Invitation impossible.');
    } finally {
      setSaving(false);
    }
  };

  const changeMemberRole = async (member, enterpriseRoleId) => {
    if (!canChangeMemberRole(member)) return;
    setError('');
    try {
      await updateEnterpriseMember(enterpriseId, member.memberId, { enterpriseRoleId });
      await load();
    } catch (e) {
      setError(e?.message || 'Mise à jour impossible.');
    }
  };

  const isEnterpriseAdminMember = (member) =>
    member.enterpriseRole?.isSystemAdmin === true || member.role === 'admin';

  const canRemoveMember = (member) => {
    if (platformAdmin) return true;
    return !isEnterpriseAdminMember(member);
  };

  const canChangeMemberRole = (member) => {
    if (platformAdmin) return true;
    return !isEnterpriseAdminMember(member);
  };

  const handleRemove = async (member) => {
    if (!canRemoveMember(member)) return;
    if (!window.confirm(`Retirer ${member.email} ?`)) return;
    try {
      await removeEnterpriseMember(enterpriseId, member.memberId);
      await load();
    } catch (e) {
      setError(e?.message || 'Suppression impossible.');
    }
  };

  return (
    <div className="space-y-4 max-w-xl">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold text-foreground">Équipe</h1>
        <Button type="button" size="sm" variant="outline" onClick={() => setShowInvite((v) => !v)}>
          {showInvite ? 'Fermer' : 'Inviter'}
        </Button>
      </div>

      {showInvite ? (
        <form onSubmit={handleInvite} className="rounded-lg border border-border p-4 space-y-3">
          <Input
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            label="Mot de passe"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Rôle</label>
            <select
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={inviteRoleId}
              onChange={(e) => setInviteRoleId(e.target.value)}
              required
            >
              {roles.map((r) => (
                <option key={r.roleId} value={r.roleId}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" size="sm" disabled={saving || !inviteRoleId}>
            {saving ? '…' : 'Ajouter'}
          </Button>
        </form>
      ) : null}

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : members.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun membre.</p>
      ) : (
        <ul className="rounded-lg border border-border divide-y divide-border">
          {members.map((m) => (
            <li key={m.memberId} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
              <div className="min-w-0 flex items-start gap-2">
                <button
                  type="button"
                  className="p-1.5 rounded-md border border-border hover:bg-muted shrink-0 mt-0.5"
                  title="QR HubUP membre"
                  aria-label={`QR HubUP pour ${m.email}`}
                  onClick={() => setQrMember(m)}
                >
                  <Icon name="QrCode" size={16} className="text-muted-foreground" />
                </button>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{m.email}</p>
                  <EnterpriseRoleBadge role={m.enterpriseRole} className="mt-1" />
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <select
                  className="text-xs rounded-md border border-border bg-background px-2 py-1.5 max-w-[140px] shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={m.enterpriseRole?.roleId || m.enterpriseRoleId || ''}
                  onChange={(e) => changeMemberRole(m, e.target.value)}
                  disabled={!canChangeMemberRole(m)}
                  aria-label={`Rôle de ${m.email}`}
                >
                  {roles.map((r) => (
                    <option key={r.roleId} value={r.roleId}>
                      {r.name}
                    </option>
                  ))}
                </select>
                {canRemoveMember(m) ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => handleRemove(m)}>
                    Retirer
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {qrMember ? (
        <CompanyMemberHubupQrModal
          user={{
            _id: qrMember.userId,
            email: qrMember.email,
          }}
          fetchQr={() =>
            fetchEnterpriseMemberHubupQr(enterpriseId, qrMember.memberId)
          }
          onClose={() => setQrMember(null)}
        />
      ) : null}
    </div>
  );
}
